import React, { useState, useEffect, useCallback } from 'react';
import config from '../../config';
import PricingSnapshotFields from './PricingSnapshotFields';
import LinkedCallLogs from '../../components/LinkedCallLogs';
import LeadRegionBadge from './LeadRegionBadge';
import s from './Leads.module.scss';

const VEHICLE_CLASS_MAP = {
  1: 'Piaggio Ape', 2: 'Tata Ace', 3: 'Ashok Leyland Dost', 4: 'Force Tempo',
  5: 'Tata 407', 6: 'Eicher Pro 1049', 7: 'Eicher Pro 1059', 8: 'Eicher Pro 2080',
  9: 'Partner', 10: 'Tata LPT 1109', 11: 'Furio 18', 12: 'Eicher Pro 6032',
  13: 'Tata LPT 1818', 14: 'Ashok Leyland 2525', 15: 'Tata Signa 3523',
};

const EQUIPMENT_CLASS_MAP = {
  51: 'JCB - Backhoe Loader', 52: 'Chota JCB', 53: 'Mini Excavator',
  54: 'Excavator', 55: 'Crane - 12 Ton', 56: 'Crane - 14 Ton', 57: 'Crane - 15 Ton',
};

const getLeadAddressDisplay = (addr) => {
  if (!addr) return '';
  const parts = [addr.locality, addr.city, addr.state].filter(Boolean);
  return parts.join(', ') || addr.text || `Address #${addr.id}`;
};

const formatDateTime = (dateTimeString) => {
  if (!dateTimeString) return 'N/A';
  const date = new Date(dateTimeString);
  return date.toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};

const getLeadDropAddresses = (lead) => {
  const dropAddresses = Array.isArray(lead?.drop_addresses) ? lead.drop_addresses.filter(Boolean) : [];
  if (dropAddresses.length > 0) return dropAddresses;
  const dropStops = Array.isArray(lead?.drop_stops)
    ? lead.drop_stops.map((stop) => stop?.location_name).filter(Boolean)
    : [];
  if (dropStops.length > 0) return dropStops;
  if (lead?.drop_address) return [lead.drop_address];
  return [];
};

const LeadDetailPanel = ({
  lead, colSpan, expandedData, editing, editForm, editSaving,
  onStartEdit, onSaveEdit, onCancelEdit, onEditFormChange,
  notes, noteDraft, noteSaving, onNoteDraftChange, onAddNote,
  activities, conversation, conversationLoading, conversationError,
  onSnooze, onToggleExpand, onEditPricing,
}) => {
  // ── Address search state for edit form (must be before early return) ──
  const [pickupAddrQuery, setPickupAddrQuery] = useState('');
  const [pickupAddrSuggestions, setPickupAddrSuggestions] = useState([]);
  const [pickupAddrSearching, setPickupAddrSearching] = useState(null);
  const [pickupAddrDisplay, setPickupAddrDisplay] = useState(null);
  const [dropAddrQuery, setDropAddrQuery] = useState('');
  const [dropAddrSuggestions, setDropAddrSuggestions] = useState([]);
  const [dropAddrSearching, setDropAddrSearching] = useState(null);
  const [dropAddrList, setDropAddrList] = useState([]);

  const handlePickupSearch = useCallback(async () => {
    const q = pickupAddrQuery.trim();
    if (q.length < 3) return;
    setPickupAddrSearching('pickup');
    try {
      const res = await fetch(`${config.customerApiBase}/resolve-location`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ address_text: q }),
      });
      if (!res.ok) { setPickupAddrSearching(null); return; }
      const data = await res.json();
      setPickupAddrSuggestions(data.results || []);
    } catch { /* ignore */ }
    setPickupAddrSearching(null);
  }, [pickupAddrQuery]);

  const handleDropSearch = useCallback(async () => {
    const q = dropAddrQuery.trim();
    if (q.length < 3) return;
    setDropAddrSearching('drop');
    try {
      const res = await fetch(`${config.customerApiBase}/resolve-location`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ address_text: q }),
      });
      if (!res.ok) { setDropAddrSearching(null); return; }
      const data = await res.json();
      setDropAddrSuggestions(data.results || []);
    } catch { /* ignore */ }
    setDropAddrSearching(null);
  }, [dropAddrQuery]);

  useEffect(() => {
    if (!lead) return;
    if (editing === lead.id) {
      const pickupId = editForm.pickup_address_id;
      if (pickupId) {
        setPickupAddrDisplay(`Address #${pickupId}`);
      } else {
        setPickupAddrDisplay(null);
      }
      if (Array.isArray(editForm.drop_address_ids) && editForm.drop_address_ids.length > 0) {
        setDropAddrList(
          editForm.drop_address_ids.map((id) => ({ id, text: `Address #${id}` }))
        );
      } else {
        setDropAddrList([]);
      }
    }
  }, [editing, lead?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!lead) return null;

  const leadNotes = notes[lead.id] || [];
  const leadActivities = activities[lead.id] || [];
  const hasPricingSnapshot = [
    lead.customer_expectation_rate,
    lead.offered_rate,
    lead.best_transporter_rate,
    lead.best_transporter_name,
  ].some((value) => value != null && value !== '');
  const leadConversation = conversation[lead.id];
  const conversationMessages = Array.isArray(leadConversation?.messages)
    ? leadConversation.messages.filter((msg) => {
        const role = msg?.role || 'bot';
        const content = msg?.content != null ? String(msg.content) : '';
        return role !== 'tool_call' && role !== 'tool_response' && !content.startsWith('[TOOL]:') && !content.includes('<ToolCall>');
      })
    : [];

  const handlePickupSelect = (suggestion) => {
    const display = getLeadAddressDisplay(suggestion);
    setPickupAddrDisplay(display);
    setPickupAddrSuggestions([]);
    setPickupAddrQuery('');
    onEditFormChange({ pickup_address_id: suggestion.id });
  };

  const handlePickupClear = () => {
    setPickupAddrDisplay(null);
    setPickupAddrQuery('');
    setPickupAddrSuggestions([]);
    onEditFormChange({ pickup_address_id: '' });
  };

  const handleDropSelect = (suggestion) => {
    const display = getLeadAddressDisplay(suggestion);
    const updated = [...dropAddrList, { id: suggestion.id, text: display }];
    setDropAddrList(updated);
    setDropAddrSuggestions([]);
    setDropAddrQuery('');
    onEditFormChange({ drop_address_ids: updated.map((a) => a.id) });
  };

  const handleDropRemove = (idx) => {
    const updated = dropAddrList.filter((_, i) => i !== idx);
    setDropAddrList(updated);
    onEditFormChange({ drop_address_ids: updated.length > 0 ? updated.map((a) => a.id) : [] });
  };

  return (
    <tr>
      <td colSpan={colSpan ?? 8} className={s.detailPanelCell}>
        <div className={s.detailPanel}>
          <div className={s.detailLeft}>
            {editing === lead.id ? (
              <div className={s.editCard}>
                {/* Lead Type */}
                <div className={s.formField}>
                  <label className={s.formLabel}>Lead Type</label>
                  <select
                    className={s.formInput}
                    value={editForm.lead_type || ''}
                    onChange={(e) => onEditFormChange({ lead_type: e.target.value })}
                  >
                    <option value="future_booking">Future Booking</option>
                    <option value="part_load_interest">Part Load Interest</option>
                    <option value="general_inquiry">General Inquiry</option>
                    <option value="equipment_interest">Equipment Interest</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                {/* Contact By */}
                <div className={s.formField}>
                  <label className={s.formLabel}>Contact By</label>
                  <input
                    className={s.formInput}
                    type="datetime-local"
                    value={editForm.contact_by || ''}
                    onChange={(e) => onEditFormChange({ contact_by: e.target.value })}
                  />
                </div>

                {/* Pickup Time */}
                <div className={s.formField}>
                  <label className={s.formLabel}>Pickup Time</label>
                  <input
                    className={s.formInput}
                    type="datetime-local"
                    value={editForm.pickup_time || ''}
                    onChange={(e) => onEditFormChange({ pickup_time: e.target.value })}
                  />
                </div>

                {/* Pickup Address */}
                <div className={s.formField}>
                  <label className={s.formLabel}>Pickup Address</label>
                  {pickupAddrDisplay ? (
                    <div className={s.addressChip}>
                      <span><i className="fa fa-check-circle" />{pickupAddrDisplay}</span>
                      <button type="button" className={s.addressChipClear} onClick={handlePickupClear}><i className="fa fa-times" /></button>
                    </div>
                  ) : (
                    <div className={s.addressRow}>
                      <input
                        className={s.formInput}
                        type="text"
                        placeholder="Search pickup address..."
                        value={pickupAddrQuery}
                        onChange={(e) => { setPickupAddrQuery(e.target.value); setPickupAddrSuggestions([]); }}
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handlePickupSearch(); } }}
                      />
                      <button type="button" className={s.addressSearchBtn} onClick={handlePickupSearch} disabled={pickupAddrQuery.trim().length < 3 || pickupAddrSearching === 'pickup'}>
                        {pickupAddrSearching === 'pickup' ? <i className="fa fa-spinner fa-spin" /> : 'Search'}
                      </button>
                    </div>
                  )}
                  {pickupAddrSuggestions.length > 0 && (
                    <div className={s.suggestionsBox}>
                      <div className={s.suggestionsHeader}><i className="fa fa-map-marker" /> Select an address:</div>
                      {pickupAddrSuggestions.map((suggestion, idx) => (
                        <button key={`${suggestion.id || 's'}-${idx}`} type="button" className={s.suggestionItem} onClick={() => handlePickupSelect(suggestion)}>
                          <strong>{suggestion.locality || suggestion.text}</strong>
                          {(suggestion.city || suggestion.state) && <small>{[suggestion.city, suggestion.state, suggestion.pin_code].filter(Boolean).join(', ')}{suggestion.similarity_score != null && <span className={s.matchScore}>{Math.round(suggestion.similarity_score * 100)}% match</span>}</small>}
                        </button>
                      ))}
                      <button type="button" className={s.suggestionsClose} onClick={() => setPickupAddrSuggestions([])}>Close</button>
                    </div>
                  )}
                </div>

                {/* Drop Address */}
                <div className={s.formField}>
                  <label className={s.formLabel}>Drop Addresses</label>
                  {dropAddrList.length > 0 && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '8px' }}>
                      {dropAddrList.map((addr, idx) => (
                        <div key={idx} className={s.dropAddressItem}>
                          <span style={{ flex: 1 }}>{addr.text}</span>
                          <button type="button" className={s.addressChipClear} onClick={() => handleDropRemove(idx)}><i className="fa fa-times" /></button>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className={s.addressRow}>
                    <input
                      className={s.formInput}
                      type="text"
                      placeholder="Search drop address..."
                      value={dropAddrQuery}
                      onChange={(e) => { setDropAddrQuery(e.target.value); setDropAddrSuggestions([]); }}
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleDropSearch(); } }}
                    />
                    <button type="button" className={s.addressSearchBtn} onClick={handleDropSearch} disabled={dropAddrQuery.trim().length < 3 || dropAddrSearching === 'drop'}>
                      {dropAddrSearching === 'drop' ? <i className="fa fa-spinner fa-spin" /> : 'Search'}
                    </button>
                  </div>
                  {dropAddrSuggestions.length > 0 && (
                    <div className={s.suggestionsBox}>
                      <div className={s.suggestionsHeader}><i className="fa fa-map-marker" /> Select an address:</div>
                      {dropAddrSuggestions.map((suggestion, idx) => (
                        <button key={`${suggestion.id || 's'}-${idx}`} type="button" className={s.suggestionItem} onClick={() => handleDropSelect(suggestion)}>
                          <strong>{suggestion.locality || suggestion.text}</strong>
                          {(suggestion.city || suggestion.state) && <small>{[suggestion.city, suggestion.state, suggestion.pin_code].filter(Boolean).join(', ')}{suggestion.similarity_score != null && <span className={s.matchScore}>{Math.round(suggestion.similarity_score * 100)}% match</span>}</small>}
                        </button>
                      ))}
                      <button type="button" className={s.suggestionsClose} onClick={() => setDropAddrSuggestions([])}>Close</button>
                    </div>
                  )}
                </div>

                {/* Vehicle Class */}
                <div className={s.formField}>
                  <label className={s.formLabel}>Vehicle Class</label>
                  <select
                    className={s.formInput}
                    value={editForm.vehicle_class_id || ''}
                    onChange={(e) => onEditFormChange({ vehicle_class_id: e.target.value ? parseInt(e.target.value) : '' })}
                  >
                    <option value="">-- Select Vehicle --</option>
                    <optgroup label="Trucks">
                      {Object.entries(VEHICLE_CLASS_MAP).map(([id, name]) => (
                        <option key={id} value={id}>Class {id} - {name}</option>
                      ))}
                    </optgroup>
                    <optgroup label="Equipment">
                      {Object.entries(EQUIPMENT_CLASS_MAP).map(([id, name]) => (
                        <option key={id} value={id}>Class {id} - {name}</option>
                      ))}
                    </optgroup>
                  </select>
                </div>

                {/* Remarks - full width */}
                <div style={{ gridColumn: '1 / -1' }}>
                  <div className={s.formField} style={{ marginBottom: 0 }}>
                    <label className={s.formLabel}>Remarks</label>
                    <textarea
                      className={s.formInput}
                      rows={3}
                      value={editForm.remarks || ''}
                      onChange={(e) => onEditFormChange({ remarks: e.target.value })}
                    />
                  </div>
                </div>

                <div style={{ gridColumn: '1 / -1' }}>
                  <PricingSnapshotFields
                    value={editForm}
                    onChange={onEditFormChange}
                  />
                </div>

                {/* Actions */}
                <div style={{ gridColumn: '1 / -1', display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                  <button
                    className={s.modalCancelBtn}
                    onClick={onCancelEdit}
                  >
                    Cancel
                  </button>
                  <button
                    className={s.modalConfirmBtn}
                    onClick={() => onSaveEdit(lead.id)}
                    disabled={editSaving}
                  >
                    {editSaving ? <i className="fa fa-spinner fa-spin" /> : <i className="fa fa-check" />} Save
                  </button>
                </div>
              </div>
            ) : (
              <div className={s.infoTiles}>
                {(lead.region_class === 'long_trip' || lead.region_class === 'outside') && (
                  <div className={s.infoTile}>
                    <div className={s.infoTileIcon} style={{ background: lead.region_class === 'long_trip' ? '#ffedd5' : '#f3f4f6', color: lead.region_class === 'long_trip' ? '#9a3412' : '#4b5563' }}>
                      <i className="fa fa-map-signs" />
                    </div>
                    <div className={s.infoTileBody}>
                      <div className={s.infoTileLabel}>Region</div>
                      <div className={s.infoTileValue}>
                        <LeadRegionBadge lead={lead} />
                      </div>
                    </div>
                  </div>
                )}
                <div className={s.infoTile}>
                  <div className={s.infoTileIcon} style={{ background: '#eff6ff', color: '#3b82f6' }}>
                    <i className="fa fa-map-marker" />
                  </div>
                  <div className={s.infoTileBody}>
                    <div className={s.infoTileLabel}>Pickup Location</div>
                    <div className={s.infoTileValue}>
                      {lead.pickup_locality
                        ? `${lead.pickup_locality}, ${lead.pickup_city || ''}`
                        : (lead.pickup_address_id ? `Address #${lead.pickup_address_id}` : 'Not specified')}
                    </div>
                  </div>
                </div>
                <div className={s.infoTile}>
                  <div className={s.infoTileIcon} style={{ background: '#f5f3ff', color: '#8b5cf6' }}>
                    <i className="fa fa-flag" />
                  </div>
                  <div className={s.infoTileBody}>
                    <div className={s.infoTileLabel}>Drop Locations</div>
                    <div className={s.infoTileValue}>
                      {(() => {
                        const drops = getLeadDropAddresses(lead);
                        const dropIds = Array.isArray(lead.drop_address_ids) ? lead.drop_address_ids : [];
                        if (drops.length > 0) return drops.map((addr, idx) => <div key={idx}>{addr}</div>);
                        if (dropIds.length > 0) return dropIds.map((id) => `Address #${id}`).join(', ');
                        return 'Not specified';
                      })()}
                    </div>
                  </div>
                </div>
                {lead.pickup_time && (
                  <div className={s.infoTile}>
                    <div className={s.infoTileIcon} style={{ background: '#fff7ed', color: '#f59e0b' }}>
                      <i className="fa fa-clock-o" />
                    </div>
                    <div className={s.infoTileBody}>
                      <div className={s.infoTileLabel}>Pickup Time</div>
                      <div className={s.infoTileValue}>{formatDateTime(lead.pickup_time)}</div>
                    </div>
                  </div>
                )}
                {lead.vehicle_class_id && (
                  <div className={s.infoTile}>
                    <div className={s.infoTileIcon} style={{ background: '#ecfdf5', color: '#10b981' }}>
                      <i className="fa fa-truck" />
                    </div>
                    <div className={s.infoTileBody}>
                      <div className={s.infoTileLabel}>Vehicle Class</div>
                      <div className={s.infoTileValue}>{VEHICLE_CLASS_MAP[lead.vehicle_class_id] || EQUIPMENT_CLASS_MAP[lead.vehicle_class_id] || `Class #${lead.vehicle_class_id}`}</div>
                    </div>
                  </div>
                )}
                {lead.remarks && (
                  <div className={`${s.infoTile} ${lead.remarks.length > 80 ? s.infoTileFull : ''}`}>
                    <div className={s.infoTileIcon} style={{ background: '#fef2f2', color: '#ef4444' }}>
                      <i className="fa fa-file-text-o" />
                    </div>
                    <div className={s.infoTileBody}>
                      <div className={s.infoTileLabel}>Remarks</div>
                      <div className={s.infoTileValue} style={{ whiteSpace: 'pre-wrap' }}>{lead.remarks}</div>
                    </div>
                  </div>
                )}
                {hasPricingSnapshot && (
                  <div className={s.pricingSummary}>
                    <strong><i className="fa fa-line-chart" /> Pricing snapshot</strong>
                    {lead.customer_expectation_rate != null && (
                      <span>Expected ₹{Number(lead.customer_expectation_rate).toLocaleString('en-IN')}</span>
                    )}
                    {lead.offered_rate != null && (
                      <span>Our offer ₹{Number(lead.offered_rate).toLocaleString('en-IN')}</span>
                    )}
                    {lead.best_transporter_rate != null && (
                      <span>Transporter ₹{Number(lead.best_transporter_rate).toLocaleString('en-IN')}</span>
                    )}
                    {lead.best_transporter_name && <span>{lead.best_transporter_name}</span>}
                  </div>
                )}
                <div className={s.metaBar}>
                  <div><i className="fa fa-calendar-o" /> Created: {formatDateTime(lead.created_at)}</div>
                  {lead.follow_up_at && (
                    <div><i className="fa fa-clock-o" /> Follow up at: {formatDateTime(lead.follow_up_at)}</div>
                  )}
                  {lead.converted_booking_id && (
                    <div><i className="fa fa-check-circle" style={{ color: '#10b981' }} /> Converted to Booking #{lead.converted_booking_id}</div>
                  )}
                  {lead.status === 'dropped' && (
                    <div><i className="fa fa-archive" style={{ color: '#ef4444' }} /> Dropped on: {formatDateTime(lead.dropped_at)}{lead.dropped_reason ? ` - ${lead.dropped_reason}` : ''}</div>
                  )}
                  <button
                    className={s.pricingLink}
                    onClick={(e) => { e.stopPropagation(); onEditPricing?.(lead); }}
                    title="Record customer expectation, our offer and the best transporter rate"
                  >
                    <i className="fa fa-line-chart" /> {hasPricingSnapshot ? 'Update pricing' : 'Add pricing'}
                  </button>
                  <button className={s.editLink} onClick={() => onStartEdit(lead)}>
                    <i className="fa fa-pencil" /> Edit
                  </button>
                </div>
              </div>
            )}
            {editing !== lead.id && (lead.status === 'new' || lead.status === 'follow_up') && (
              <div className={s.snoozeRow}>
                <span className={s.snoozeLabel}>Snooze:</span>
                {[1, 3, 7].map((d) => (
                  <button
                    key={d}
                    className={s.snoozeBtn}
                    onClick={(e) => { e.stopPropagation(); onSnooze(lead.id, d); }}
                  >
                    +{d}d
                  </button>
                ))}
              </div>
            )}
            {editing !== lead.id && lead.id && (
              <div style={{ marginTop: 16 }}>
                <LinkedCallLogs entityType="lead" entityId={lead.id} />
              </div>
            )}
            {editing !== lead.id && (
              <div className={s.activitySection}>
                <div className={s.sectionHeader}>
                  <i className="fa fa-history" /> Activity
                </div>
                {leadActivities.length === 0 && (
                  <div className={s.emptyHint}>No activity yet.</div>
                )}
                <div className={s.activityList}>
                  {leadActivities.map((act) => {
                    const eventLabels = {
                      created: 'Lead created',
                      status_changed: `Status changed ${act.old_value ? `from ${act.old_value}` : ''} to ${act.new_value}`,
                      converted: `Converted to booking ${act.details?.converted_booking_id ? `#${act.details.converted_booking_id}` : ''}`,
                      dropped: `Dropped${act.details?.dropped_reason ? `: ${act.details.dropped_reason}` : ''}`,
                      snoozed: 'Contact by date updated',
                      edited: 'Lead details edited',
                      note_added: 'Note added',
                      follow_up_scheduled: `Follow-up scheduled${act.details?.follow_up_at ? ` for ${formatDateTime(act.details.follow_up_at)}` : ''}`,
                    };
                    const iconMap = {
                      created: 'fa-plus', status_changed: 'fa-exchange', converted: 'fa-check',
                      dropped: 'fa-trash',
                      snoozed: 'fa-clock-o', edited: 'fa-pencil', note_added: 'fa-comment',
                      follow_up_scheduled: 'fa-clock-o',
                    };
                    const colorMap = {
                      created: '#10b981', status_changed: '#f59e0b', converted: '#3b82f6',
                      dropped: '#ef4444',
                      snoozed: '#8b5cf6', edited: '#6b7280', note_added: '#ec4899',
                      follow_up_scheduled: '#8b5cf6',
                    };
                    const color = colorMap[act.event_type] || '#9ca3af';
                    return (
                      <div key={act.id} className={s.activityItem}>
                        <div className={s.activityIcon} style={{ background: color }}>
                          <i className={`fa ${iconMap[act.event_type] || 'fa-circle'}`} />
                        </div>
                        <div className={s.activityBody}>
                          <div className={s.activityText}>{eventLabels[act.event_type] || act.event_type}</div>
                          <div className={s.activityTime}>{formatDateTime(act.created_at)}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            {editing !== lead.id && (
              <div className={s.notesSection}>
                <div className={s.sectionHeader}>
                  <i className="fa fa-sticky-note" /> Follow-up Notes
                </div>
                {leadNotes.length === 0 && (
                  <div className={s.emptyHint}>No notes yet.</div>
                )}
                <div className={s.notesList}>
                  {leadNotes.map((note) => (
                    <div key={note.id} className={s.noteCard}>
                      <div className={s.noteText}>{note.note_text}</div>
                      <div className={s.noteMeta}>
                        <i className="fa fa-clock-o" /> {formatDateTime(note.created_at)}
                      </div>
                    </div>
                  ))}
                </div>
                <div className={s.noteInputRow}>
                  <textarea
                    className={s.noteTextarea}
                    placeholder="Add a follow-up note..."
                    value={noteDraft[lead.id] || ''}
                    onChange={(e) => onNoteDraftChange(lead.id, e.target.value)}
                  />
                  <button
                    className={s.noteAddBtn}
                    onClick={() => onAddNote(lead.id)}
                    disabled={noteSaving[lead.id]}
                  >
                    {noteSaving[lead.id] ? <i className="fa fa-spinner fa-spin" /> : <i className="fa fa-plus" />}
                  </button>
                </div>
              </div>
            )}
          </div>
          {editing !== lead.id && (
            <div className={s.detailRight}>
              <div className={s.convHeader}>
                <i className="fa fa-comments" /> Conversation
              </div>
              {!lead.conversation_id ? (
                <div className={s.convHint}>No conversation linked to this lead.</div>
              ) : conversationLoading[lead.id] ? (
                <div className={s.convLoading}>
                  <i className="fa fa-spinner fa-spin" /> Loading conversation...
                </div>
              ) : conversationError[lead.id] ? (
                <div className={s.convHint} style={{ color: '#ef4444' }}>
                  <i className="fa fa-exclamation-circle" /> {conversationError[lead.id]}
                </div>
              ) : !leadConversation || conversationMessages.length === 0 ? (
                <div className={s.convHint}>No messages yet.</div>
              ) : (
                <div className={s.convScroll}>
                  {conversationMessages.map((msg, idx) => {
                    const role = msg?.role || 'bot';
                    const rawContent = msg?.content != null ? String(msg.content) : '';
                    const label = role === 'user' ? 'Customer' : role === 'operator' ? 'Operator' : 'Bot';
                    const bubbleClass = role === 'user' ? s.user : role === 'operator' ? s.operator : s.bot;
                    const align = role === 'user' ? 'flex-end' : 'flex-start';
                    return (
                      <div key={msg.id || idx} style={{ display: 'flex', justifyContent: align }}>
                        <div className={`${s.convBubble} ${bubbleClass}`}>
                          <div className={s.convLabel}>{label}</div>
                          <div className={s.convText}>{rawContent}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      </td>
    </tr>
  );
};

export default LeadDetailPanel;
